import type { Client, Entry, FixedFee } from "@ascente/shared-types";
import { parseDate, monthsBetweenDates, quartersBetweenDates } from "./dateHelpers.js";
import { calculateGST } from "./formatters.js";

export interface ClientTotals {
  hours: number;
  hourlyTotal: number;
  taskCharges: number;
  fixedTotal: number;
  subtotal: number;
  gst: number;
  total: number;
  entries: Entry[];
}

/**
 * Totals for a group of clients across one date range, built by summing
 * each client's {@link ClientTotals}. Dashboard and Analytics both
 * produce this shape from {@link calcClientTotals} so neither computes
 * its own month count or its own GST.
 */
export interface TotalsSummary {
  byClient: Record<string, ClientTotals>;
  grandSubtotal: number;
  grandGST: number;
  grandTotal: number;
  totalHours: number;
  totalHourlyCharges: number;
  totalTaskCharges: number;
  totalFixedCharges: number;
}

/** Check whether a fixed fee is active for a given date range. */
export function isFixedFeeActive(fee: FixedFee, rangeStart: Date, rangeEnd: Date): boolean {
  if (!fee.startDate && !fee.endDate) return true;
  const fs = fee.startDate ? parseDate(fee.startDate) : new Date(0);
  const fe = fee.endDate ? parseDate(fee.endDate) : new Date(9999, 11, 31);
  return fs <= rangeEnd && fe >= rangeStart;
}

/** Filter entries to only those within the date range (inclusive). */
export function filterEntriesByDateRange(entries: Entry[], start: Date, end: Date): Entry[] {
  return entries.filter((e) => {
    const d = parseDate(e.date);
    return d >= start && d <= end;
  });
}

/**
 * Sum task charges, treating `hours` as a quantity multiplier.
 *
 * Task-based clients reuse the hours field to mean "how many of this
 * thing", so a 3-hour entry at $200 bills $600. A zero or missing value
 * means one occurrence, not zero.
 */
export function calcTaskCharges(entries: Entry[]): number {
  return entries.reduce((sum, e) => sum + (e.hours || 1) * (e.taskCharge || 0), 0);
}

/**
 * Amount owed for one fixed fee across a date range.
 *
 * The previous single-file app filtered every fee list to
 * frequency === "monthly", so quarterly and some_months fees silently
 * billed nothing. Both are handled here. per_occurrence is deliberately
 * not billed — see its case below.
 *
 * @param someMonthsOptIn Names of some_months fees the user ticked for
 *   this invoice. A some_months fee is never billed automatically.
 */
export function calcFixedFeeAmount(
  fee: FixedFee,
  entries: Entry[],
  rangeStart: Date,
  rangeEnd: Date,
  someMonthsOptIn: string[],
): number {
  if (!isFixedFeeActive(fee, rangeStart, rangeEnd)) return 0;

  switch (fee.frequency) {
    case "monthly":
      return fee.amount * monthsBetweenDates(rangeStart, rangeEnd);

    case "quarterly":
      // Once per quarter the range touches. Confirmed by the business
      // owner: "the quarterly website fee for four corners only happens
      // quarterly" — the fee recurs, so a six-month invoice owes it
      // twice. A range shorter than a quarter still owes it once; the
      // fee is not pro-rated. The invoice shows the quarter count as the
      // line's quantity, so an unexpected multiple is visible before
      // sending.
      return fee.amount * quartersBetweenDates(rangeStart, rangeEnd);

    case "per_occurrence":
      // Never billed automatically, by the owner's decision: "don't auto
      // charge that 200 depending on what's typed. We'll input the task
      // ourselves and the amount as an entry."
      //
      // Per-occurrence work is logged as an ordinary entry carrying its own
      // task charge, which calcTaskCharges already bills. Matching a fee
      // against free text a human typed would bill $200 or $0 depending on
      // wording, and would double-bill if the entry also carried a charge.
      //
      // The frequency remains a valid stored value so existing client
      // records still load. A fee saved with it contributes nothing.
      return 0;

    case "some_months":
      return someMonthsOptIn.includes(fee.name) ? fee.amount : 0;
  }
}

/**
 * Whether a client has anything that could go on an invoice for this range:
 * at least one entry, a fixed fee that could apply automatically (active
 * for the range), or a `some_months` fee that could be opted into.
 *
 * `calcClientTotals(...).total` is not enough to gate an "Invoice" button
 * on: a `some_months` fee bills nothing until it is ticked, and that
 * checkbox lives inside the invoice screen itself. Gating on `total` alone
 * would make such a client's only billable item unreachable — the screen
 * that turns it on is the screen a zero total refuses to open.
 */
export function canInvoiceClient(
  client: Client,
  allEntries: Entry[],
  rangeStart: Date,
  rangeEnd: Date,
): boolean {
  const hasEntries =
    filterEntriesByDateRange(
      allEntries.filter((e) => e.clientId === client.id),
      rangeStart,
      rangeEnd,
    ).length > 0;
  if (hasEntries) return true;
  return client.fixedFees.some(
    (f) =>
      // per_occurrence contributes nothing, so it cannot make an otherwise
      // empty invoice worth opening.
      f.frequency !== "per_occurrence" &&
      (f.frequency === "some_months" || isFixedFeeActive(f, rangeStart, rangeEnd)),
  );
}

/**
 * Every figure the app shows for one client across one date range.
 *
 * Dashboard, Analytics, and invoice generation all call this, so the
 * three cannot drift apart the way they previously had.
 */
export function calcClientTotals(
  client: Client,
  allEntries: Entry[],
  rangeStart: Date,
  rangeEnd: Date,
  someMonthsOptIn: string[] = [],
): ClientTotals {
  const entries = filterEntriesByDateRange(
    allEntries.filter((e) => e.clientId === client.id),
    rangeStart,
    rangeEnd,
  );

  const hours = entries.reduce((sum, e) => sum + e.hours, 0);
  const billsHourly = client.type === "hourly" || client.type === "hourly_fixed";
  const hourlyTotal = billsHourly ? hours * client.rate : 0;
  const taskCharges = billsHourly ? 0 : calcTaskCharges(entries);

  const fixedTotal = client.fixedFees.reduce(
    (sum, f) => sum + calcFixedFeeAmount(f, entries, rangeStart, rangeEnd, someMonthsOptIn),
    0,
  );

  const subtotal = hourlyTotal + taskCharges + fixedTotal;
  const gst = calculateGST(subtotal);

  return { hours, hourlyTotal, taskCharges, fixedTotal, subtotal, gst, total: subtotal + gst, entries };
}
